import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from "@angular/forms";
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { Faq, FaqTranslation } from "../../../../../core/models/faq.model";
import { Cultures } from "../../../../../core/constants/cultures";
import { BaseComponent } from "../../../../../shared/base-component";
import { InputSelectComponent } from "../../../../../shared/input-select/input-select.component";
import { TrialDetail } from "../../../../../core/models/trial-detail.model";
import { SelectOption } from "../../../../../core/models/select-option.model";
import { Countries } from "../../../../../core/constants/countries";
import { FaqService } from "../../../../../core/services/faq.service";
import { requiredNoWhitespaceValidator } from "../../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-faq-form',
  templateUrl: './faq-form.component.html',
  styleUrl: './faq-form.component.scss'
})
export class FaqFormComponent extends BaseComponent {
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('cultureSelect') cultureSelect: InputSelectComponent;

  @Input() trial: TrialDetail;

  @Output() updated = new EventEmitter<Faq[]>();

  faq: Faq = new Faq();

  form: FormGroup = new FormGroup({
    isBusy: new FormControl<boolean>(false),
    selectedCulture: new FormControl<string>('en'),
    question: new FormControl<string>('', requiredNoWhitespaceValidator()),
    answer: new FormControl<string>('', requiredNoWhitespaceValidator())
  });

  cultureForms: FormGroup[] = [];
  availableCultures: { value: string, text: string }[] = [];
  filteredCultures: { value: string, text: string }[] = [];

  allCountryOptions: SelectOption[] = Countries.all().map(c => new SelectOption(c.code, c.name));
  filteredCountries: SelectOption[] = this.allCountryOptions;

  constructor(private _faqService: FaqService) {
    super();

    Cultures.all().forEach(culture => {
      this.availableCultures.push({ value: culture.culture, text: culture.name });
    });
    this.filteredCultures = this.availableCultures;
  }

  buildCultureForms() {
    // Build culture forms
    this.cultureForms = [];
    this.availableCultures.forEach(culture => {
      const translation = this.faq.translations.find(t => t.culture === culture.value);
      if (culture.value !== 'en') {
        this.cultureForms.push(new FormGroup({
          id: new FormControl<string>(translation?.id),
          culture: new FormControl<string>(culture.value),
          question: new FormControl<string>(translation?.question),
          answer: new FormControl<string>(translation?.answer),
        }));
      }
    });
  }

  add() {
    this.faq = new Faq();

    this.form.patchValue({
      isBusy: false,
      selectedCulture: 'en',
      question: '',
      answer: '',
    });

    this.buildCultureForms();
    this.form.markAsUntouched();

    this.modal.show();
  }

  update(faq: Faq) {
    this.faq = faq;

    this.form.patchValue({
      question: this.faq.question,
      answer: this.faq.answer,
      isBusy: false,
      selectedCulture: 'en',
    });

    this.buildCultureForms();

    this.modal.show();
  }

  onCountryFiltered(value: string) {
    if (value && value !== '') {
      this.filteredCountries = this.allCountryOptions.sort((a, b) => {
        const aMatches = a.text.toLowerCase().includes(value.toLowerCase());
        const bMatches = b.text.toLowerCase().includes(value.toLowerCase());
        if (aMatches && !bMatches) {
          return -1; // Move `a` before `b`
        } else if (!aMatches && bMatches) {
          return 1; // Move `b` before `a`
        }
      });
      return;
    }

    this.filteredCountries = this.allCountryOptions.sort((a, b) => {
      return a.text.localeCompare(b.text);
    });
  }

  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();

    this.filteredCultures = this.availableCultures.filter(x =>
      x.text.toLowerCase().includes(filterValue)
    );
  }

  onSubmitForm() {
    if (!this.form.valid) {
      // Mark all controls as touched to show validation errors
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key).markAsTouched();
      });
      return;
    }

    this.form.patchValue({isBusy: true});

    // Form is valid, proceed with submission
    this.faq.question = this.form.value.question;
    this.faq.answer = this.form.value.answer;

    if (!this.faq.translations)
      this.faq.translations = [];

    this.cultureForms.forEach(cultureForm => {
      const translation = this.faq.translations.find(t => t.culture == cultureForm.value.culture);
      if (!translation) {
        this.faq.translations.push(new FaqTranslation({
          culture: cultureForm.value.culture,
          question: cultureForm.value.question,
          answer: cultureForm.value.answer
        }));
      } else {
        translation.question = cultureForm.value.question;
        translation.answer = cultureForm.value.answer;
      }
    });

    if (this.faq.id) {
      // Update
      this._faqService.update(this.faq.id, this.faq).subscribe({
        next: faqs => {
          this.form.patchValue({isBusy: false});
          this._alertService.showSuccessAlert('FAQ updated successfully');
          this.updated.emit(faqs);
          this.modal.hide();
        },
        error: err => {
          console.log(err);
          this.form.patchValue({isBusy: false});
          this._alertService.showErrorAlert(err);
        }
      });
    } else {
      // Add
      this._faqService.add(this.trial.id, this.faq).subscribe({
        next: faqs => {
          this.form.patchValue({isBusy: false});
          this._alertService.showSuccessAlert('FAQ added successfully');
          this.updated.emit(faqs);
          this.modal.hide();
        },
        error: err => {
          console.log(err);
          this.form.patchValue({isBusy: false});
          this._alertService.showErrorAlert(err);
        }
      });
    }
  }
}
